import Teammo from '@teammo/teammo-api';
import handleErrorDispatch from 'app/utils/handlers/handleError';
import { normalize as normalizr } from 'normalizr';
import { getCurrentTeam } from 'app/ducks/teams/selectors';
import { getJwtToken, getUserId } from 'app/ducks/authentication/selectors';
import { IPagination, IStoredUser } from 'app/types/common';
import _ from 'lodash';
import { createStructuredSelector } from 'reselect';
import localStorage from 'local-storage';

async function TeammoApi(state, organisationId = null, groupId = null, raw = false, skipDeserialize = false, extraHeaders = {}): Promise<any> {
  const jwtToken = await getJwtToken();
  const currentTeam = getCurrentTeam(state);
  const resultOrganisationId = organisationId === null
    ? (!_.isEmpty(currentTeam)
          && !_.isEmpty(currentTeam.organisation)
          && currentTeam.organisation.id)
        || state.organisations.currentOrganisationId
    : organisationId;
  const resultUserId = state.authentication?.userId !== null
    ? state.authentication.userId
    : state.authentication.user?.id;

  let currentTeamGroupId;
  if (!_.isEmpty(currentTeam) && !_.isEmpty(currentTeam.group)) {
    currentTeamGroupId = currentTeam.group.id;
  }
  // console.log(currentTeam);
  // console.log(groupId);

  const resultGroupId = groupId === null ? currentTeamGroupId : groupId;

  if (raw) {
    return Teammo(
      ENVIRONMENT,
      jwtToken,
      resultUserId,
      null,
      false,
      null,
      null,
      skipDeserialize,
      extraHeaders,
    );
  }
  return Teammo(
    ENVIRONMENT,
    jwtToken,
    resultUserId,
    null,
    false,
    resultOrganisationId,
    resultGroupId,
    skipDeserialize,
    extraHeaders,
  );
}

function normalize(data, schema, debug = false): any {
  const parsedData = data.data;
  const normalizedData = normalizr(parsedData, schema);

  if (debug) {
    console.log(normalizedData); // eslint-disable-line no-console
  }

  return normalizedData;
}

function generateSortConfig(sortConfig, sortKey): any {
  const currentKey = sortConfig[sortKey];
  let newKey = '';

  switch (currentKey) {
    case undefined:
      newKey = 'asc';
      break;
    case 'none':
      newKey = 'asc';
      break;
    case 'asc':
      newKey = 'desc';
      break;
    case 'desc':
      newKey = null;
      break;
    default:
      newKey = null;
      break;
  }

  const newConfig = { [sortKey]: newKey };
  return newConfig;
}

function convertTableSort(sort): any {
  return _.mapValues(
    _.keyBy(sort, (object) => object.id),
    (object) => (object.desc ? 'desc' : 'asc'),
  );
}

// Convert to dash case, but not the dot (for nested sorting)
function convertToDashCaseWithoutDots(string): any {
  return string
    .replace(/[^a-zA-Z0-9\.]+/g, '-')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([0-9])([^0-9])/g, '$1-$2')
    .replace(/([^0-9])([0-9])/g, '$1-$2')
    .replace(/-+/g, '-')
    .toLowerCase();
}

function compilePaginationObject(pagination: IPagination): any {
  const sort = convertTableSort(pagination.sort);
  // console.log('sort', sort);
  const sortAttributes = [];
  _.map(sort, (value, key) => {
    const translatedKey = convertToDashCaseWithoutDots(key);
    const sortString = value === 'asc' ? `${translatedKey}` : `-${translatedKey}`;
    sortAttributes.push(sortString);
  });

  const filterAttributes = {};
  _.map(pagination.filter, (value, key) => {
    filterAttributes[convertToDashCaseWithoutDots(value.id)] = value.value;
  });

  const finalSortString = sortAttributes.join(',');
  // console.log('final', finalSortString);

  // const includeArr = pagination.includes || [];
  // const includeString = includeArr.join(',');

  return {
    page: { ...pagination.page, number: pagination.page.number + 1 },
    sort: finalSortString,
    filter: filterAttributes,
    // include: includeString,
  };
}

async function getStoredUser(): Promise<IStoredUser> {
  let storedUser = {};
  const userId = await getUserId();
  if (!_.isEmpty(userId)) {
    storedUser = localStorage.get(userId);
  }
  if (_.isEmpty(storedUser) || userId === undefined) {
    storedUser = {};
  }
  return storedUser;
}

export {
  TeammoApi,
  handleErrorDispatch,
  normalize,
  generateSortConfig,
  createStructuredSelector,
  compilePaginationObject,
  convertTableSort,
  getStoredUser,
};
